import React, { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent } from "@mui/material";
import { Icons, Typography, IconButton, Stack, TextField, Button, Grow } from "@curves/ui-base";
import ProductInfo from "@icoach/pos/components/ProductInfo";
import { isFunction } from "@util/utils";

/**
 *  商品加入確認彈窗組件
 * @param {Object} props - 組件屬性
 * @param {boolean} props.open - 是否顯示彈窗
 * @param {Object} props.sourceData - 商品資料
 * @param {Function} props.onClose - 關閉回調
 * @param {Function} props.refresh - 刷新回調
 */
const ProductAddedConfirmDialog = ({ open = false, sourceData, onClose: onCloseProps, refresh }) => {
	const { product, timestamp } = sourceData || {};
	// 內部狀態：管理編輯中的價格與數量
	const [editPrice, setEditPrice] = useState(0);
	const [editQuantity, setEditQuantity] = useState(1);
	// 自動關閉延遲時間（秒）
	const autoCloseDelay = 3;

	// 倒數計時狀態
	const [countdown, setCountdown] = useState(autoCloseDelay);

	// 編輯模式狀態
	const [isEditMode, setIsEditMode] = useState(false);

	// 追蹤上次的 timestamp，用於判斷是否需要重置
	const lastTimestampRef = useRef(null);

	// 數量增減輔助函式
	const handleQuantityChange = delta => {
		const newQuantity = editQuantity + delta;
		if (newQuantity >= 1) {
			setEditQuantity(newQuantity);
		}
	};

	// 確認修改
	const handleConfirm = () => {
		// 這裡可以處理確認邏輯，例如更新購物車

		isFunction(refresh) &&
			refresh({
				...product,
				currentPrice: editPrice,
				quantity: editQuantity,
			});

		handleOnClose();
	};

	const handleOnClose = () => {
		isFunction(onCloseProps) && onCloseProps();
		// 關閉後 focus 非輸入元素，讓 IME 回到英文狀態
		setTimeout(() => {
			const el = document.createElement("div");
			el.tabIndex = -1;
			el.style.cssText = "position:fixed;left:-9999px;top:-9999px;width:0;height:0;opacity:0;pointer-events:none;";
			document.body.appendChild(el);
			el.focus();
			requestAnimationFrame(() => document.body.removeChild(el));
		}, 350);
	};

	// 取消修改
	const handleCancel = () => {
		// 重置編輯狀態
		setIsEditMode(false);
		handleOnClose();
	};

	// 點擊視窗進入編輯模式
	const handleDialogClick = () => {
		if (!isEditMode) {
			setIsEditMode(true);
			setCountdown(0); // 停止倒數顯示
		}
	};

	// 對話框開啟時重置狀態，每次開啟都使用最新的 feedback 資料
	useEffect(
		() => {
			if (open) {
				// 每次開啟都重置狀態，確保使用最新的 feedback 資料
				setEditPrice(product?.currentPrice || 0);
				setEditQuantity(product?.quantity || 1);
				setCountdown(autoCloseDelay);
				setIsEditMode(false);
				lastTimestampRef.current = timestamp;
			}
		},
		// eslint-disable-next-line
		[open, sourceData]
	);

	// 倒數計時效果
	useEffect(
		() => {
			let timeoutId;
			if (open) {
				const setCountTime = sec => {
					if (sec < 0 && !isEditMode) {
						handleOnClose();
						return;
					} else if (isEditMode) {
						return;
					}
					setCountdown(sec);
				};
				timeoutId = setTimeout(() => setCountTime(countdown - 1), 1000);
			}
			return () => {
				clearTimeout(timeoutId);
			};
		},
		// eslint-disable-next-line
		[open, isEditMode, countdown]
	);

	return (
		<Dialog
			key={timestamp}
			open={open}
			onClose={handleOnClose}
			PaperProps={{ className: "wx-80rem" }}
			onClick={handleDialogClick} // 添加點擊處理
			TransitionComponent={Grow} // 添加 Grow 過場效果
			TransitionProps={{
				timeout: 300, // 過場時間 300ms
			}}
		>
			<DialogContent className="p-4">
				<Stack className="product-setting-content py-2" justifyContent={"start"} alignItems={"center"} spacing={2}>
					<ProductInfo product={product} />
					<Stack className="price-edit-group" justifyContent={"start"} alignItems={"center"} spacing={1}>
						<Icons.DollarSign className="text-primary" />
						<TextField value={editPrice} onChange={e => setEditPrice(Number(e.target.value))} inputProps={{ min: 0 }} maskType="MONEY" />
					</Stack>
					<Stack className="quantity-edit-group" justifyContent={"start"} alignItems={"center"}>
						<Button size="small" onClick={() => handleQuantityChange(-1)} disabled={editQuantity <= 1}>
							<Icons.Minus className="text-secondary" />
						</Button>
						<TextField value={editQuantity} onChange={e => setEditQuantity(Number(e.target.value))} inputProps={{ min: 1 }} maskType="MONEY" />
						<Button size="small" onClick={() => handleQuantityChange(1)}>
							<Icons.Plus className="text-secondary" />
						</Button>
					</Stack>
				</Stack>

				{/* 倒數提示文字 - 只在非編輯模式下顯示 */}
				{!isEditMode && (
					<Typography variant="body2" className="text-center mt-4 font-color-3">
						{countdown > 0 ? `${countdown}秒後回到掃描模式...或是進行商品編輯` : "即將回到掃描模式..."}
					</Typography>
				)}

				{/* 操作按鈕 - 只在編輯模式下顯示 */}
				{isEditMode && (
					<Stack direction="row" spacing={2} className="mt-4" justifyContent="end">
						<Button variant="contained" onClick={handleConfirm}>
							確認
						</Button>
					</Stack>
				)}

				{/* 關閉按鈕 */}
				<IconButton
					onClick={handleCancel}
					style={{
						position: "absolute",
						top: "8px",
						right: "8px",
						padding: "4px",
					}}
				>
					<Icons.ErrorCircle style={{ fontSize: "32px" }} />
				</IconButton>
			</DialogContent>
		</Dialog>
	);
};

export default ProductAddedConfirmDialog;
